/** AutoCAD's Modify commands, as a grammar the studio can drive.
 *
 * ⚠⚠ **A COMMAND IS A SEQUENCE OF PROMPTS, AND THAT IS THE WHOLE OF AutoCAD'S FEEL.** Select,
 * name the command, answer what it asks: base point, second point, angle, factor. Every one of
 * these differs only in what it asks for and what it does with the answers — so they are a TABLE
 * rather than five state machines, and adding OFFSET or ARRAY is a row.
 *
 * ⚠ The prompts are AutoCAD's own wording, angle brackets and all. A drafter reads that strip
 * without looking away from the drawing, and "Specify second point or <use first point as
 * displacement>:" is recognised at a glance in a way that "Click the end point" is not.
 *
 * ⚠⚠ **A TYPED NUMBER BEATS A DRAGGED ONE, WHICH IS WHY `scalar` EXISTS.** ROTATE by 90 and
 * SCALE by 2 are typed, always, by everyone — dragging a scale factor on a 115-metre site plan
 * means the factor is the distance from the base point in millimetres, which is AutoCAD's real
 * rule and is unusable at that size. So a command that takes a number says so, the dynamic-input
 * box appears, and the drag stays available for the cases where it reads well.
 */
import type { EditOp } from "./api";
import { givenOf } from "./givens";

export type { Pt } from "./givens";
import type { Pt } from "./givens";

/** `[a, b, c, d, e, f]` — `x' = a·x + c·y + e`, `y' = b·x + d·y + f`. */
export type Affine = [number, number, number, number, number, number];

export const IDENTITY: Affine = [1, 0, 0, 1, 0, 0];

/** Rotation by `rad` about `p`. */
export function rotateAt(p: Pt, rad: number): Affine {
  const c = Math.cos(rad), s = Math.sin(rad);
  return [c, s, -s, c, p.x - c * p.x + s * p.y, p.y - s * p.x - c * p.y];
}

/** Uniform scale by `f` about `p`. */
export function scaleAt(p: Pt, f: number): Affine {
  return [f, 0, 0, f, p.x * (1 - f), p.y * (1 - f)];
}

/** Reflection across the line through `a` and `b`.
 *  ⚠ Degenerate input returns the identity rather than NaNs — the two points are equal for one
 *  frame every time a drafter starts a MIRROR, and a canvas full of NaN draws nothing at all. */
export function mirrorAcross(a: Pt, b: Pt): Affine {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-12) return IDENTITY;
  const ux = dx / len, uy = dy / len;
  const m00 = ux * ux - uy * uy, m01 = 2 * ux * uy;
  return [m00, m01, m01, -m00,
          a.x - m00 * a.x - m01 * a.y,
          a.y - m01 * a.x + m00 * a.y];
}

export interface CmdDef {
  /** The `op` the API knows it by. */
  op: EditOp;
  /** How many points before it can run. */
  points: number;
  /** AutoCAD's prompt for each point, in order. */
  prompts: string[];
  /** A number the drafter may type instead of, or as well as, dragging the last point. */
  scalar?: { label: string; suffix: string; def: number };
  /** ⚠⚠ **THE NUMBER COMES FIRST.** AutoCAD's OFFSET asks for the distance BEFORE it asks
   *  which side, because the side is a click and the distance is not — and a command table that
   *  assumed points-then-number could not express it. */
  scalarFirst?: boolean;
  /** The live preview: the points so far, the cursor, and the typed number if there is one. */
  preview: (pts: Pt[], cur: Pt, n: number | null) => Affine;
  /** What `/edit/` is sent. */
  params: (pts: Pt[], cur: Pt, n: number | null) => Record<string, unknown>;
  /** ⚠ Whether the command CREATES objects, so the studio knows to clear the selection after
   *  it — AutoCAD leaves nothing selected when COPY and MIRROR finish. */
  creates?: boolean;
}

const deg = (a: Pt, b: Pt) => (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;

export const COMMANDS: Record<string, CmdDef> = {
  move: {
    op: "move", points: 2,
    // ⚠⚠ **`[Displacement]` IS BACK, AND THIS TIME AGAINST A REFERENCE.** It was removed with
    // the note "nothing reads it — this app has no typed input at a point step", which was true
    // then and is not now: `finishCmdPoint` hands a typed letter to `parseTyped` with the
    // prompt's own keywords, which is how LINE hears `U` and PLINE hears `A`.
    // ⚠ The wording is BricsCAD V26's, read off the running program rather than remembered —
    // `Enter base point [Displacement] <Displacement>:`. See
    // `qa/reference/bricscad-v26-grammar.md`.
    prompts: ["Specify base point or [Displacement] <Displacement>:",
              "Specify second point or <use first point as displacement>:"],
    preview: (p, c) => [1, 0, 0, 1, c.x - p[0].x, c.y - p[0].y],
    params: (p, c) => ({ delta: [c.x - p[0].x, c.y - p[0].y, 0] }),
  },
  copy: {
    op: "copy", points: 2, creates: true,
    // ⚠ BricsCAD's own pair, both answered now: `Displacement` takes the vector directly and
    // `mode` chooses one copy against many. Its default is Multiple, and so is this one.
    prompts: ["Specify base point or [Displacement/mOde] <Displacement>:",
              "Specify second point or <use first point as displacement>:"],
    preview: (p, c) => [1, 0, 0, 1, c.x - p[0].x, c.y - p[0].y],
    params: (p, c) => ({ delta: [c.x - p[0].x, c.y - p[0].y, 0] }),
  },
  rotate: {
    op: "rotate", points: 2,
    // ⚠ `[Copy]` removed — rotating a COPY needs the copy and the rotation to be one undo
    // step, which is a backend op that does not exist. `[Reference]` stays because the code
    // that answers it landed in the same edit: see `REF_ASK` and `commitTyped`.
    prompts: ["Specify base point:",
              "Specify rotation angle or [Reference] <0>:"],
    // ⚠ AutoCAD's angle is ABSOLUTE — the direction from the base point to the cursor, not the
    // change since the last frame. Dragging round the base spins the selection, which is what
    // makes the number on screen and the picture agree.
    scalar: { label: "Angle", suffix: "°", def: 0 },
    preview: (p, c, n) => rotateAt(p[0], ((n ?? deg(p[0], c)) * Math.PI) / 180),
    params: (p, c, n) => ({ base: [p[0].x, p[0].y, 0], angle: n ?? deg(p[0], c) }),
  },
  scale: {
    op: "scale", points: 2,
    prompts: ["Specify base point:",
              "Specify scale factor or [Reference] <1.0000>:"],
    // ⚠⚠ **AutoCAD'S DRAGGED FACTOR IS THE DISTANCE FROM THE BASE POINT IN DRAWING UNITS**, so
    // on a plan drawn in millimetres a click 3 m away is ×3000. That is not a bug being copied
    // for its own sake: it is why every drafter types this one, and a "helpful" reinterpretation
    // would make pdcad's SCALE mean something different from the SCALE in their hands.
    scalar: { label: "Scale", suffix: "×", def: 1 },
    preview: (p, c, n) => scaleAt(p[0], n ?? (Math.hypot(c.x - p[0].x, c.y - p[0].y) || 1)),
    params: (p, c, n) => ({ base: [p[0].x, p[0].y, 0],
                            factor: n ?? (Math.hypot(c.x - p[0].x, c.y - p[0].y) || 1) }),
  },
  stretch: {
    op: "stretch", points: 4,
    prompts: ["Specify first corner of crossing window:",
              "Specify opposite corner:",
              "Specify base point:",
              "Specify second point or <use first point as displacement>:"],
    // ⚠⚠ **NO PREVIEW, DELIBERATELY.** This machinery previews by applying ONE affine to the
    // whole selection, which is exactly what STRETCH does not do — it moves the vertices inside
    // the window and leaves the rest. A preview that dragged the entire wall would be a picture
    // of MOVE, and a drafter would trust it right up until they let go.
    preview: () => [1, 0, 0, 1, 0, 0],
    params: (p, c) => ({ p1: [p[0].x, p[0].y, 0], p2: [p[1].x, p[1].y, 0],
                         delta: [c.x - p[2].x, c.y - p[2].y, 0] }),
  },
  offset: {
    op: "offset", points: 1, creates: true, scalarFirst: true,
    // ⚠ `[Exit/Multiple/Undo]` removed. Multiple repeats the offset until you stop; this app
    // offsets the whole selection from one click and says so below, so there is nothing to
    // repeat. Exit and Undo are Escape and Ctrl+Z, which already work.
    prompts: ["Specify point on side to offset:"],
    scalar: { label: "Offset", suffix: "", def: 1 },
    // ⚠ No preview transform: OFFSET does not move the selection, it makes a parallel copy
    // beside it. Showing the selection shifted would be a picture of the wrong command.
    // ⚠⚠ **ONE SIDE POINT FOR THE WHOLE SELECTION, WHERE AutoCAD ASKS PER OBJECT.** AutoCAD's
    // OFFSET loops — "Select object to offset:" then "Specify point on side" — so each object
    // gets its own answer. pdcad already has a selection when the command starts, so the click
    // is applied to all of them, and each decides its own side from it: a circle grows if the
    // click is outside it, a polyline takes whichever parallel lands nearer. That is a real
    // difference from AutoCAD and it is here rather than hidden.
    preview: () => IDENTITY,
    params: (_p, c, n) => ({ distance: n ?? 1, side: [c.x, c.y, 0] }),
  },
  mirror: {
    op: "mirror", points: 2, creates: true,
    prompts: ["Specify first point of mirror line:",
              "Specify second point of mirror line:"],
    preview: (p, c) => mirrorAcross(p[0], c),
    // ⚠ `erase: false` — AutoCAD's *"Erase source objects? [Yes/No] <N>"* defaults to No, so
    // MIRROR makes a mirrored COPY. See `edits.do_mirror`.
    params: (p, c) => ({ p1: [p[0].x, p[0].y, 0], p2: [c.x, c.y, 0], erase: false }),
  },
};

/** ⚠ Exported so `Studio` can say "ROTATE is not built yet" for the rest of the Modify panel
 *  rather than letting a live-looking button do nothing. */
export const BUILT: ReadonlySet<string> = new Set(Object.keys(COMMANDS));

/** Prompts that belong to no command TABLE — spoken by `Studio` from wherever the command
 *  happens to be handled.
 *
 * ⚠⚠⚠ **A GUARD THAT WALKS TABLES CANNOT SEE A STRING LITERAL, AND FIVE UNANSWERED KEYWORDS
 * WERE HIDING IN THESE.** `test/keywords.test.ts` was written after 31 were found in `DRAWS`,
 * then extended to `COMMANDS` after eleven more were found there — and INSERT was still
 * offering `[Basepoint/Scale/Rotate]` and UCS `[World/Previous]` from `sayRef.current?.("...")`
 * calls, where nothing could look at them. Three rounds of the same defect in three places the
 * guard did not reach.
 * ⇒ Every prompt with an option list lives HERE now, in one table, and the guard walks it. A
 * prompt with no keywords may still be written inline; one with keywords cannot hide.
 */
export const PROMPTS: Record<string, string> = {
  // ⚠ Answered by `ZOOM_OPTION` in `Studio.tsx` — and only these eight. BricsCAD V26 offers
  // twelve (`qa/reference/bricscad-v26-grammar.md`); Center, Dynamic, Left and Right are not
  // built, so they are not printed. A prompt is a promise.
  zoom: "Enter a scale factor (nX), or "
      + "[zoom In/zoom Out/All/Center/Extents/Left/Previous/Right/Scale/Window/ENtity] "
      + "<Extents>:",
  // ⚠ Both answered — `resetAxes` is UCSWORLD and `ucsPrev` is UCSPREV, and both existed as
  // ribbon buttons long before the prompt could hear the letter.
  ucs: "Specify origin of UCS or [World/Previous]:",
  // ⚠ `[Basepoint/Scale/Rotate]` REMOVED. The Insert dialog has already asked for the scale and
  // the rotation by the time this prompt appears — the keywords offered a second way in to
  // answers the drafter had just given, and no code read them anyway.
  insert: "Specify insertion point:",
  arcNext: "Specify endpoint of arc or [Line/Close/Undo]:",
  polyNext: "Specify next point or [Arc/Close/Undo]:",
};

// ── DRAW ─────────────────────────────────────────────────────────────────────────────────────

export interface DrawDef {
  /** What `edits.do_draw` calls it. */
  kind: string;
  /** How many points, or -1 for "until Enter" — a polyline. */
  points: number;
  /** AutoCAD's prompts, in order. ⚠ The LAST one repeats for an unbounded tool. */
  prompts: string[];
  /** ⚠⚠ **WHAT IT ASKS FOR AFTER ITS POINTS.** TEXT is placed, then given a height, then given
   *  its string — three prompts, in AutoCAD's order. A tool table that only counted points
   *  could not express a tool whose last two answers are typed. */
  asks?: { key: string; label: string; kind: "number" | "text"; prompt: string }[];
  /** ⚠ A default worked out from the drawing rather than a constant: a text height of 2.5 is
   *  right on a millimetre detail and invisible on a 115-metre site plan. */
  /** ⚠ `pts` is the CLICKS SO FAR, and the arc flyout is why it is here: a chord length's
   *  sensible default is the distance between the two points already picked, which no function
   *  of the drawing's extent can know. */
  defaultOf?: (key: string, extent: number, pts: readonly Pt[]) => number;
  /** ⚠⚠ **THE TOOL MEASURES AN OBJECT, SO IT NEEDS ONE PICKED BEFORE IT ARMS.** AutoCAD's
   *  radius, diameter and arc-length dimensions all open with *"Select arc or circle:"* — the
   *  clicks that follow only say where the leader goes. A tool with this set does NOT clear the
   *  selection when it arms (every other draw tool does), and refuses in words when what is
   *  selected cannot be measured. */
  ref?: readonly string[];
}

/** ⚠⚠ **THE MODE NAMES ARE THE CHROME'S, NOT OURS.** `handlers.ts` maps `draw.line` → mode
 *  `"line"` through `MODE_OF`, and the ribbon, the menus and the classic toolbars all go through
 *  it. Keying this table on those strings is what makes the real Draw panel live rather than a
 *  second set of buttons beside it.
 *
 *  ⚠ Each gesture is AutoCAD's DEFAULT one: a circle is centre then a point on it, an arc is
 *  three points along the curve, a rectangle is two opposite corners. The flyouts offer eleven
 *  other givens for arcs and circles; those are not built and the ribbon says so. */
export /** ⚠⚠⚠ **THESE PROMPTS USED TO ADVERTISE 31 KEYWORDS THIS APP CANNOT ANSWER.**
 *  RECT printed `[Chamfer/Elevation/Fillet/Thickness/Width]`, ARC printed `[Center]` and then
 *  `[Center/End]`, MTEXT printed `[Height/Justify/Line spacing/Rotation/Style/Width]`, LINE
 *  printed `[Undo]` and PLINE `[Arc/Close/Undo]`. Typing any of them did nothing at all — and
 *  not because the letter was ignored: **there is no typed input at a POINT step.** Points come
 *  from clicks; the dynamic-input box takes a NUMBER, for a tool's `asks`. So the bracket was
 *  not a keyword the app declined, it was a keyword the app could not hear.
 *
 *  ⚠ `SELECT_PROMPT` in this same file already states the rule and follows it — "the options
 *  this app actually answers to. Listing ones it does not would be the same lie as a live button
 *  that does nothing." These were the same lie, printed at every draw. Removed, 31 of them.
 *
 *  ⚠⚠ **THIS IS A TRIM, NOT THE FEATURE.** AutoCAD's option grammar — `ARC`, then `C`, then a
 *  point, then `A`, then `90` — is what makes a fast drafter fast, and `docs/GAP.md` calls it a
 *  Phase-B BASE rather than a Phase-C polish for exactly that reason. Building it needs a prompt
 *  engine that can take a letter at a point step and switch the tool underneath: the ARC and
 *  CIRCLE keywords already have real givens behind them (`givens.ts` — `cse`, `sce`, `sed`,
 *  `cd`), so those are the cheap half, and the rest is new work. When it lands, the keyword goes
 *  back into the prompt IN THE SAME EDIT as the code that answers it, so the two cannot drift.
 */
const DRAWS: Record<string, DrawDef> = {
  // ⚠⚠ **`[Undo]` AND `[Close/Undo]` ARE BACK BECAUSE THE CODE THAT ANSWERS THEM LANDED IN THE
  // SAME EDIT** — the rule the comment above lays down. `U` steps one point back and stays in
  // the command; `C` closes the polyline through `do_draw`'s own `closed` flag. The other 29
  // keywords removed in that trim — RECT's Chamfer/Fillet/Width, MTEXT's Justify/Style, PLINE's
  // Arc — are still unanswered and therefore still absent.
  line: { kind: "LINE", points: 2,
          prompts: ["Specify first point:", "Specify next point or [Undo]:"] },
  polyline: { kind: "LWPOLYLINE", points: -1,
              prompts: ["Specify start point:",
                        "Specify next point or [Arc/Close/Undo]:"] },
  circle: { kind: "CIRCLE", points: 2,
            prompts: ["Specify center point for circle or [3P/2P/Ttr (tan tan radius)]:",
                      "Specify radius of circle:"] },
  arc: { kind: "ARC", points: 3,
         prompts: ["Specify start point of arc:",
                   "Specify second point of arc:",
                   "Specify end point of arc:"] },
  rect: { kind: "RECT", points: 2,
          // ⚠⚠ **ALL NINE OF BricsCAD V26's, and the nine abbreviate apart** — C F W D S A R E T.
          // `qa/reference/bricscad-v26-grammar.md`.
          // ⚠ `Dimensions`, `Square` and `Area` are answered ENTIRELY here: each makes the second
          // corner derived, so the click chooses only which way the rectangle goes.
          // ⚠ `Rotated`, `Elevation` and `Thickness` go to the server. Rotation is done in the
          // rectangle's OWN frame — see `rectCorners` here and `_rect_corners` there, which
          // project the two picks onto the rotated axes and share the answer.
          prompts: ["Specify first corner point or "
                    + "[Chamfer/Fillet/Width/Dimensions/Square/Area/Rotated/Elevation/"
                    + "Thickness]:",
                    "Specify other corner point:"] },
  // ⚠⚠ BricsCAD V26 prints seven on TEXT; pdcad printed none. These three need nothing but a
  // keystroke — the point that follows becomes the ALIGNMENT point rather than the insert, which
  // is what justification means in DXF. `Center horizontally`, `Middle (hor/vert)` and
  // `Right justify` are BricsCAD's own spellings, and their capitals are what they answer to:
  // C, M and R, all distinct.
  // ⚠ `use defined Style` sets the TEXTSTYLE sysvar — the same variable the ribbon's style
  // picker writes, so the two cannot disagree about which font the next label gets.
  //
  // ⚠⚠ **`Align on line` AND `Fit between points` SPAN TWO POINTS AND ARE THE ONLY TWO THAT DO.**
  // ALIGNED scales both ways, so the letters keep their shape; FIT keeps the height and condenses
  // only the width. Drawing either one for both gives text of the right LENGTH and the wrong
  // SIZE, which reads as a font problem.
  // ⚠ The scaling is decided in the CANVAS, because how much to squeeze depends on how wide the
  // words measure in the font actually loaded and the server cannot know that. `display.py`
  // sends both ends — FORMAT 34 — and `Canvas2D` does the arithmetic.
  // ⚠ Neither appears in this office's own imports: of 6,743 TEXT entities, ZERO use halign 3 or
  // 5. Built anyway — the corpus argues against SPENDING TIME, not against being complete, and
  // the same argument had already been wrong once about SPLINE's `Knots`.
  // ⚠ `Justification options...` is a submenu of the same set.
  text: { kind: "TEXT", points: 1,
          // ⚠ BricsCAD writes `Middle (hor/vert)`, and that parenthetical contains a SLASH,
          // which is the character `keywordsOf` splits option lists on. The bracket is a hint
          // about what middle means, not part of the keyword — so it is dropped rather than
          // smuggled past the parser with a look-alike character, which would leave a prompt
          // nobody could copy and a keyword nobody could grep for.
          prompts: ["Specify start point of text or "
                    + "[Center horizontally/Middle/Right justify/use defined Style/"
                    + "Align on line/Fit between points]:",
                    // ⚠ The SECOND point, asked for only by Align and Fit — see `TEXT_JUSTIFY`.
                    "Specify the end of the line the text spans:"],
          asks: [{ key: "height", label: "Height", kind: "number",
                   prompt: "Specify height <{d}>:" },
                 { key: "text", label: "Text", kind: "text", prompt: "Enter text:" }],
          // ⚠ 1/200 of the drawing, snapped to something a drafter would type. A 6-metre room
          // gets 30 mm text and a 115-metre site plan gets 500 — neither is a guess a constant
          // could make.
          defaultOf: (_k, extent) => {
            const raw = Math.max(1, extent) / 200;
            const pow = Math.pow(10, Math.floor(Math.log10(raw)));
            const n = raw / pow;
            return (n > 5 ? 5 : n > 2 ? 2.5 : n > 1 ? 2 : 1) * pow;
          } },
  mtext: {
    kind: "MTEXT", points: 2,
    prompts: ["Specify first corner:",
              "Specify opposite corner:"],
    // ⚠ The BOX is the point — a multi-line note placed by one point has no width and does not
    // wrap, which is the only thing that makes it multi-line.
    // ⚠⚠ **NO `text` ASK — THE EDITOR IS THE GESTURE.** It used to be typed at the command
    // line, one line at a time with `\n` for a new line: this app's own invention, and the
    // reason a note with two paragraphs was a chore. AutoCAD asks for the BOX and then gives you
    // the editor inside it, which is what `author` does now for a new MTEXT.
    asks: [{ key: "height", label: "Height", kind: "number", prompt: "Specify height <{d}>:" }],
    defaultOf: (_k, extent) => {
      const raw = Math.max(1, extent) / 200;
      const pow = Math.pow(10, Math.floor(Math.log10(raw)));
      const n = raw / pow;
      return (n > 5 ? 5 : n > 2 ? 2.5 : n > 1 ? 2 : 1) * pow;
    },
  },
  mview: {
    kind: "MVIEW", points: 2,
    prompts: ["Specify corner of viewport:", "Specify opposite corner:"],
    // ⚠ 0 means FIT, and the server SAYS which standard scale it chose — a window at 1:73.4 is
    // not one anyone can measure off a sheet.
    asks: [{ key: "scale", label: "Scale 1:", kind: "number",
             prompt: "Specify viewport scale 1:<0 to fit>:" }],
    defaultOf: () => 0,
  },
  leader: {
    kind: "LEADER", points: -1,
    prompts: ["Specify leader arrowhead location:",
              "Specify next point, or press Enter for the text:"],
    // ⚠ The HEIGHT is asked for, not left to the server's default. A note at 1/200 of the
    // drawing is right for a whole plan and invisible on a detail, and the drafter is the one
    // who knows which they are annotating.
    asks: [{ key: "height", label: "Height", kind: "number", prompt: "Specify text height <{d}>:" },
           { key: "text", label: "Text", kind: "text", prompt: "Enter the note:" }],
    defaultOf: (_k, extent) => {
      const raw = Math.max(1, extent) / 200;
      const pow = Math.pow(10, Math.floor(Math.log10(raw)));
      const n = raw / pow;
      return (n > 5 ? 5 : n > 2 ? 2.5 : n > 1 ? 2 : 1) * pow;
    },
  },
  // ⚠⚠ **MLEADER, AND THE RIBBON BUTTON ALREADY SAID SO.** `anno.leader` is labelled
  // "Multileader" with `cmd: MLEADER`, and it armed the mode that draws the 1980s `LEADER`
  // entity. Measured across 11,734 of the office's drawings: `MULTILEADER` in 23.0%, `LEADER`
  // in 29.2% — both live, so the old one stays reachable by typing LEADER, and the button that
  // says multileader makes one.
  // ⚠ Two points: the arrow first, then where the note sits. Which SIDE the text attaches to is
  // decided by the server from those two — a leader that doubles back through its own text is
  // not a choice anybody wants offered.
  mleader: {
    kind: "MLEADER", points: 2,
    prompts: ["Specify leader arrowhead location:",
              "Specify leader landing location:"],
    asks: [{ key: "height", label: "Height", kind: "number", prompt: "Specify text height <{d}>:" },
           { key: "text", label: "Text", kind: "text", prompt: "Enter the note:" }],
    defaultOf: (_k, extent) => {
      const raw = Math.max(1, extent) / 200;
      const pow = Math.pow(10, Math.floor(Math.log10(raw)));
      const n = raw / pow;
      return (n > 5 ? 5 : n > 2 ? 2.5 : n > 1 ? 2 : 1) * pow;
    },
  },
  // ⚠ A boundary, not a rectangle. AutoCAD's default gesture is two corners, but this office's
  // drawings are full of polygonal wipeouts masking round a detail — so it takes a run of points
  // and closes on Enter, exactly like the polyline it is shaped as.
  // ⚠⚠ **MLINE — THE WALL TOOL THIS OFFICE DREW WITH FOR TWENTY YEARS.** It was READ, carried
  // and drawn from the day the importer was written; only the half that MAKES one was missing,
  // and the ribbon pointed at PLINE+OFFSET instead. Measured: in 3.8% of 17,903 drawings here.
  // ⚠ The width is ASKED FOR, with a default from the drawing's own extent — the same 1/200
  // rule that sizes new text. A constant would be a 20-unit wall on a plan in millimetres.
  mline: {
    kind: "MLINE", points: -1,
    prompts: ["Specify start point:",
              "Specify next point, or press Enter to finish:"],
    asks: [{ key: "width", label: "Width", kind: "number",
             prompt: "Specify multiline width <{d}>:" }],
    // ⚠ Rounded to something a drafter would type. A wall is 100 or 200, never 137.4.
    defaultOf: (_k, extent) => {
      const raw = Math.max(1, extent) / 100;
      const pow = Math.pow(10, Math.floor(Math.log10(raw)));
      const n = raw / pow;
      return (n > 5 ? 5 : n > 2 ? 2 : 1) * pow;
    },
  },
  // ⚠⚠ **EXPRESS TOOLS' BREAKLINE, AND THE ONE COMMAND ON THAT TAB THAT IS REALLY A DRAW
  // TOOL.** It was in `MODE_OF` from the day the tab was lifted — so it armed, took its points
  // and then said "BREAKLINE is not built yet", because nothing here answered to the mode.
  // ⚠ Two points are the ends of the break; an optional third slides the symbol along it,
  // which is what AutoCAD's "Specify location for break symbol" asks for.
  breakline: {
    kind: "BREAKLINE", points: -1,
    prompts: ["Specify first point for break line:",
              "Specify second point, or press Enter to place the symbol at the middle:"],
  },
  // ⚠⚠ **REVCLOUD WAS IN `MODE_OF` AND IN NOTHING ELSE — THE BREAKLINE BUG ABOVE, AGAIN.**
  // `"draw.revcloud": "revcloud"` armed the canvas, took its points and then said REVCLOUD is
  // not built, because no entry here answered to the mode and no `DRAW_KINDS` entry answered on
  // the server. The command survey scored it "not built" and was right.
  // ⚠ ONE mode for both of AutoCAD's gestures: two points are the CORNERS of a rectangular
  // cloud, three or more are a polygon. The flyout's Rectangular and Polygonal are the same
  // command with a different number of clicks, which is what they are in AutoCAD too.
  // ⚠ The scallop size is asked for and defaults to a fraction of the perimeter, not a constant
  // — the same reason BREAKLINE sizes itself that way. AutoCAD's default comes from DIMSCALE;
  // this office draws site plans at 1:200 and details at 1:5 in the same units.
  // ⚠⚠ BricsCAD V26 prints `[Arc length/Entity/Rectangular/Polygonal/Freehand/Style]`; pdcad
  // printed nothing while ALREADY DOING two of them by point count. Naming them is not cosmetic:
  // `Rectangular` finishes on the second point instead of waiting for Enter, which is what makes
  // a rectangular cloud two clicks rather than two clicks and a keystroke.
  // ⚠ `Arc length` was only ever asked AFTER the points; BricsCAD asks it up front, and up front
  // is where a drafter changes it — once, before drawing six clouds at the same scale.
  // ⚠ `Entity` traces something already on the drawing and REPLACES it — `revcloudentity` on
  // the server, which reads the flattened outline out of the display list so a circle, an
  // ellipse, a polyline and a spline all trace the same way.
  // ⚠ `Style` is Normal against Calligraphy — AutoCAD's calligraphic cloud draws each scallop
  // like a pen stroke, wide where the nib bites and closing to nothing, which is a per-vertex
  // WIDTH on the same polyline rather than a second entity. `xyseb`, not `xyb`.
  // ⚠⚠ `Freehand` is the ONE gesture in this app that is not a run of picks: hold the button and
  // the cloud follows the pointer, let go and it closes. `Canvas2D` grew `onTrail` for it, and
  // the samples are thinned as they ARRIVE — a pointer sends hundreds of moves a second and a
  // traced cloud would otherwise be thousands of vertices nobody can edit.
  revcloud: {
    kind: "REVCLOUD", points: -1,
    prompts: ["Specify first corner, or a point on the cloud "
              + "or [Arc length/Rectangular/Polygonal/Entity/Style/Freehand]:",
              "Specify the opposite corner, or the next point and Enter to close:"],
    asks: [{ key: "arc", label: "Arc", kind: "number",
             prompt: "Specify arc length <{d}>:" }],
    defaultOf: (_k, extent) => {
      const raw = Math.max(1, extent) / 40;
      const pow = Math.pow(10, Math.floor(Math.log10(raw)));
      const n = raw / pow;
      return (n > 5 ? 5 : n > 2 ? 2 : 1) * pow;
    },
  },
  wipeout: {
    kind: "WIPEOUT", points: -1,
    prompts: ["Specify first point of the area to mask:",
              "Specify next point, or press Enter to close it:"],
  },
  // ⚠⚠ **TWO SPLINE COMMANDS, BECAUSE AUTOCAD HAS TWO.** They make the same DXF type and a
  // different object to edit for the rest of the drawing's life: `spline` places the points the
  // curve passes THROUGH, `splinecv` the points that PULL it. AutoCAD's ribbon has both, side by
  // side, and a drafter who picks the wrong one gets grips that do not do what they expect.
  // ⚠⚠ BricsCAD V26 prints `[Entity/Method/Knots]` on SPLINE. `Method` is Fit against CV — and
  // pdcad has had BOTH as separate commands since they were written, so the keyword is wiring,
  // not geometry: it moves between the two tools already here.
  // ⚠ `Entity` converts a polyline into a spline THROUGH its corners — `splineentity` on the
  // server.
  // ⚠⚠ `Knots` really does change the curve, and the note here used to say it did not. MEASURED
  // through (0,0) (100,400) (300,400) (500,0) (700,300): the midpoint of the run comes out at
  // (300.0, 400.0) uniform, (369.7, 331.9) chord and (334.3, 358.9) square-root chord. Three
  // visibly different curves through the same five points — the argument for declining it was
  // simply wrong, and it took one call to `global_bspline_interpolation` to find out.
  spline: {
    kind: "SPLINE", points: -1,
    prompts: ["Specify first point or [Method/Entity/Knots]:",
              "Enter next point:"],
  },
  splinecv: {
    kind: "SPLINECV", points: -1,
    prompts: ["Specify first point or [Method/Entity/Knots]:",
              "Enter next point:"],
  },
  // ⚠⚠ **THREE PICKS, AND THEY ARE AutoCAD'S EXACTLY**: the two extension-line origins, then
  // pull the dimension line clear. The third click is not "somewhere on the dimension" — it is
  // the base, and its distance from the first two is the offset.
  dimension: { kind: "DIMLINEAR", points: 3,
               prompts: ["Specify first extension line origin or <select object>:",
                         "Specify second extension line origin:",
                         "Specify dimension line location or "
                         + ":"] },

  // ── the kinds that round-tripped for months and could not be MADE ──────────────────────────
  // ⚠⚠ `ELLIPSE` is in 40.3% of this office's 11,734 drawings and `SOLID` in 36.4%. Both were
  // in the document model, in `_projection` and in the grips from the start — so a client's
  // ellipse opened, selected and dragged correctly, and the Draw button said "ELLIPSE is not
  // built yet". The missing half was the arithmetic, not the hard part.
  // ⚠ AutoCAD's default ELLIPSE gesture is the two ENDS OF ONE AXIS and then a point whose
  // distance from that axis is the other radius — three picks, not centre-and-corner.
  ellipse: { kind: "ELLIPSE", points: 3,
             prompts: ["Specify axis endpoint of ellipse:",
                       "Specify other endpoint of axis:",
                       "Specify distance to other axis:"] },
  ellipsearc: { kind: "ELLIPSEARC", points: 5,
                prompts: ["Specify axis endpoint of elliptical arc:",
                          "Specify other endpoint of axis:",
                          "Specify distance to other axis:",
                          "Specify start angle:",
                          "Specify end angle:"] },
  // ⚠ AutoCAD's POINT command places one per click and STAYS ARMED until Escape — the button is
  // even labelled "Multiple Point" in the Draw menu. `onDrawPoint` re-arms it the same way LINE
  // continues from its own end.
  point: { kind: "POINT", points: 1, prompts: ["Specify a point:"] },
  // ⚠ 3 or 4 corners, ended with Enter — a SOLID is a filled triangle OR a quad and AutoCAD
  // takes both from the same command.
  solid: { kind: "SOLID", points: -1,
           prompts: ["Specify first point:", "Specify next point, or Enter when done:"] },
  polygon: { kind: "POLYGON", points: 2,
             prompts: ["Specify center of polygon:",
                       "Specify radius of circle:"],
             // ⚠ The side count and the inscribed/circumscribed choice are ASKED, because both
             // change the shape and neither is said by the two clicks. AutoCAD asks first; ours
             // asks after, which is the same two answers in the order this prompt engine has.
             asks: [{ key: "sides", label: "Sides", kind: "number",
                      prompt: "Enter number of sides <{d}>:" },
                    { key: "circumscribed", label: "C/I", kind: "number",
                      prompt: "Inscribed in circle (0) or Circumscribed about circle (1) <0>:" }],
             defaultOf: (k) => (k === "sides" ? 6 : 0) },
  donut: { kind: "DONUT", points: 1,
           prompts: ["Specify center of donut or <exit>:"],
           asks: [{ key: "inner", label: "Inside", kind: "number",
                    prompt: "Specify inside diameter <{d}>:" },
                  { key: "outer", label: "Outside", kind: "number",
                    prompt: "Specify outside diameter <{d}>:" }],
           // ⚠ Proportional to the drawing, like TEXT's height — a 0.5/1.0 donut is invisible on
           // a site plan and a 500/1000 one covers a detail.
           defaultOf: (k, extent) => roundSize(extent) * (k === "inner" ? 2 : 4) },
  // ⚠⚠ TWO ENTITIES, NOT ONE WITH A FLAG. An XLINE is infinite in both directions and a RAY in
  // one; a drafter who wanted the half-line and got the full one has a line across the whole
  // sheet to erase, every time.
  // ⚠ `[Horizontal/Vertical]` against a REFERENCE: BricsCAD V26 prints
  // `[Horizontal/Vertical/Angle/Bisect/Parallel]` on both — harvested from the running program,
  // `qa/reference/bricscad-v26-grammar.md`. pdcad offered none.
  //
  // ⚠⚠ **ALL FIVE ARE BUILT.** `Parallel` was the last because it is the only one answered by a
  // CLICK rather than the keyboard: the pick is the line to be parallel to, and it is EATEN, not
  // selected — leaving it highlighted would hand the next command an object nobody chose.
  // ⚠ `directionOf` takes the segment NEAREST THE CLICK, which is what "parallel to that wall"
  // means on an L-shaped room. A circle or a spline is refused by name rather than guessed at.
  //
  // ⚠ Horizontal, Vertical and Angle all need no second point: the direction IS the answer, so
  // one click finishes. Bisect is the opposite — it needs THREE, a vertex and a point on each
  // leg — and it is the one a drafter uses for a mitre or a corner splay.
  guide: { kind: "XLINE", points: 2,
           prompts: ["Specify a point or [Horizontal/Vertical/Angle/Bisect/Parallel]:",
                     "Specify through point:"] },
  ray: { kind: "RAY", points: 2,
         prompts: ["Specify start point or [Horizontal/Vertical/Angle/Bisect/Parallel]:",
                   "Specify through point:"] },
};

// ── SELECT, and the selection grammar its prompt is made of ──────────────────────────────────
//
// ⚠⚠ **THIS IS THE PART OF AutoCAD `docs/GAP.md` §1 SAYS THE CONTROL COUNT CANNOT SEE.** There
// is no ribbon button for a fence and there never has been: `SELECT` opens *"Select objects:"*
// and you answer with a letter. A drafter picking eleven pipes that cross a corridor draws one
// fence through them; the alternative is eleven clicks, and it is the reason the option exists.
//
// ⚠ pdcad selects NOUN-VERB — pick, then command — so `SELECT` builds the set and leaves it
// picked, rather than handing it to a command that is already running. That is a real
// difference from AutoCAD's modal prompt and it is stated here rather than hidden.

export interface SelectMode {
  /** The letters AutoCAD's prompt accepts, longest first — `WP` must beat `W`. */
  keys: readonly string[];
  label: string;
  /** How many points the gesture takes, or -1 for "until Enter". 0 is instant. */
  points: number;
  prompts: readonly string[];
}

export const SELECT_MODES: readonly SelectMode[] = [
  { keys: ["WP"], label: "WPolygon", points: -1,
    prompts: ["First polygon point:", "Specify endpoint of line or [Undo], Enter when done:"] },
  { keys: ["CP"], label: "CPolygon", points: -1,
    prompts: ["First polygon point:", "Specify endpoint of line or [Undo], Enter when done:"] },
  { keys: ["F"], label: "Fence", points: -1,
    prompts: ["Specify first fence point:",
              "Specify next fence point or [Undo], Enter when done:"] },
  { keys: ["W"], label: "Window", points: 2,
    prompts: ["Specify first corner:", "Specify opposite corner:"] },
  { keys: ["C"], label: "Crossing", points: 2,
    prompts: ["Specify first corner:", "Specify opposite corner:"] },
  { keys: ["ALL"], label: "All", points: 0, prompts: [] },
  { keys: ["P"], label: "Previous", points: 0, prompts: [] },
  { keys: ["L"], label: "Last", points: 0, prompts: [] },
];

/** ⚠ The prompt AutoCAD prints, with the options this app actually answers to. Listing ones it
 *  does not would be the same lie as a live button that does nothing. */
export const SELECT_PROMPT =
  "Select objects or [Window/Last/Crossing/ALL/Fence/WPolygon/CPolygon/Previous]:";

export function selectModeOf(typed: string): SelectMode | undefined {
  const t = typed.trim().toUpperCase();
  // ⚠ Longest key first — `WP` and `W` are different modes and a prefix match on `W` would make
  // the window option swallow the window-polygon one.
  return [...SELECT_MODES].sort((a, b) => b.keys[0].length - a.keys[0].length)
    .find((m) => m.keys.includes(t));
}

/** ⚠ The same 1/200-of-the-drawing rule TEXT, LEADER and the dimension styles already use,
 *  named once here now that a fourth tool needs it. */
/** ⚠⚠ **THE EFFECTIVE TOOL, WHICH FOR THE DIMENSION MODE DEPENDS ON THE FLYOUT.** Every place
 *  that used to read `DRAWS[mode]` goes through here instead — the point count, the prompts and
 *  whether an object must be picked first all change with `dimKind`, and a call site that read
 *  the table directly would use `points: 3` for a radius dimension that takes one. */
export function defOf(mode: string, dimKind: string, given = ""): DrawDef | undefined {
  if (mode === "dimension") return DIM_KIND[dimKind] ?? DIM_KIND.ortholinear;
  // ⚠ Before `DRAWS`: these are modes in their own right, not flyout kinds — see `CHAIN_KIND`.
  if (CHAIN_KIND[mode]) return CHAIN_KIND[mode];
  // ⚠⚠ **THE ARC AND CIRCLE FLYOUTS ARE THIRTEEN GIVENS OF TWO ENTITIES**, and each asks for a
  // different run of picks — three points, or two and an angle, or two and a chord. `givens.ts`
  // owns the arithmetic; this only turns one into the shape the draw loop already speaks.
  const g = givenOf(mode, given);
  if (g) {
    return {
      kind: g.kind, points: g.points, prompts: g.prompts,
      asks: g.ask ? [{ key: g.ask.key, label: g.ask.label, kind: "number",
                       prompt: g.ask.prompt }] : undefined,
      defaultOf: (_k, _extent, pts) => (g.ask?.def ? g.ask.def(pts as Pt[]) : 90),
    };
  }
  return DRAWS[mode];
}

/** The canonical points for a flyout given, or a sentence saying why there are none.
 *  ⚠ Returns the picks UNCHANGED when the mode has no given — so the caller does not have to
 *  know which tools are flyouts and which are not. */
export function solveGiven(mode: string, given: string, pts: Pt[], n: number): Pt[] | string {
  const g = givenOf(mode, given);
  return g ? g.solve(pts, n) : pts;
}

/** RECTANG's four corners, from the two picks and whatever its options have set.
 *
 * ⚠⚠⚠ **ONE RULE, TWO CALLERS, BECAUSE THE PREVIEW AND THE COMMIT MUST AGREE.** `Studio` uses
 * this to work out the corner it SENDS; `Canvas2D` uses it to draw the rubber band. They were
 * separate: the band was always an axis-aligned rectangle between the two points, so `Dimensions`
 * dragged one size and drew another, and `Square` — added the same day — dragged a rectangle and
 * committed a square. A drafter watching the band has no way to know which one is lying.
 *
 * ⚠ Everything happens in the rectangle's OWN frame, so the three options compose: rotate, then
 * fix the sides, then map back. `Rotated` is why the frame exists at all.
 * ⚠ The SIGN is taken from the drag and kept — the click chooses the quadrant, which is the only
 * thing left for it to choose once the sides are stated.
 * ⚠ A drag of exactly zero counts as positive, or the rectangle collapses on a click that lands
 * on its own first corner.
 */
export function rectCorners(p0: Pt, p1: Pt,
                            o: { dims?: { l: number; w: number }; square?: boolean;
                                 rot?: number } = {}): Pt[] {
  const rot = o.rot || 0;
  const c = Math.cos(rot), s = Math.sin(rot);
  const dx = p1.x - p0.x, dy = p1.y - p0.y;
  let a = dx * c + dy * s;
  let b = -dx * s + dy * c;
  const sa = a < 0 ? -1 : 1, sb = b < 0 ? -1 : 1;
  if (o.dims) { a = sa * o.dims.l; b = sb * o.dims.w; }
  else if (o.square) { const n = Math.max(Math.abs(a), Math.abs(b)); a = sa * n; b = sb * n; }
  const at = (lx: number, ly: number) => ({ x: p0.x + lx * c - ly * s,
                                            y: p0.y + lx * s + ly * c });
  return [at(0, 0), at(a, 0), at(a, b), at(0, b)];
}

export function roundSize(extent: number): number {
  const raw = Math.max(1, extent) / 200;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / pow;
  return (n > 5 ? 5 : n > 2 ? 2.5 : n > 1 ? 2 : 1) * pow;
}

/** ⚠⚠ **THE DIMENSION FLYOUT IS EIGHT COMMANDS WEARING ONE MODE, AND THEY DO NOT TAKE THE SAME
 *  PICKS.** Linear and aligned want three points; radius, diameter and arc length want an object
 *  and ONE point; angular wants four; ordinate two. The old table mapped a name to a `kind`
 *  string only, which is why `DRAWS.dimension` could carry a single `points: 3` — true for two
 *  of the eight and wrong for the rest.
 *
 *  ⚠ The keys are the OLD APP'S vocabulary, which is INVERTED relative to AutoCAD's words: its
 *  `anno.dim.linear` reports `ortholinear` and its `anno.dim.aligned` reports `linear`.
 *  Translated here, once, rather than in the studio — `handlers.ts` is 10,000 lines this app
 *  does not own and its names are not going to change. */
export const DIM_KIND: Record<string, DrawDef> = {
  ortholinear: {
    kind: "DIMLINEAR", points: 3,
    prompts: ["Specify first extension line origin or <select object>:",
              "Specify second extension line origin:",
              "Specify dimension line location or "
              + ":"],
  },
  linear: {
    kind: "DIMALIGNED", points: 3,
    prompts: ["Specify first extension line origin or <select object>:",
              "Specify second extension line origin:",
              "Specify dimension line location:"],
  },
  radius: {
    kind: "DIMRADIUS", points: 1, ref: ["CIRCLE", "ARC"],
    prompts: ["Specify dimension line location:"],
  },
  diameter: {
    kind: "DIMDIAMETER", points: 1, ref: ["CIRCLE", "ARC"],
    prompts: ["Specify dimension line location:"],
  },
  // ⚠ An ARC only. AutoCAD's DIMARC measures arc LENGTH, and a full circle has no ends to put
  // the extension lines on — so a circle is refused rather than silently measured as something.
  arc: {
    kind: "DIMARC", points: 1, ref: ["ARC"],
    prompts: ["Specify arc length dimension location,:"],
  },
  angular: {
    kind: "DIMANGULAR", points: 4,
    prompts: ["Specify angle vertex:", "Specify first angle endpoint:",
              "Specify second angle endpoint:",
              "Specify dimension arc line location:"],
  },
  // ⚠⚠ Which ORDINATE is decided by the drag, not asked — pull the leader sideways and it is
  // the Y datum, up or down and it is X. That is AutoCAD's own rule and the server applies it;
  // asking would be asking a question the gesture has already answered.
  ordinate: {
    kind: "DIMORDINATE", points: 2,
    prompts: ["Specify feature location:",
              "Specify leader endpoint:"],
  },
};

/** ⚠⚠⚠ **DIMCONTINUE, DIMBASELINE AND QDIM WERE BUILT ON THE SERVER AND NOTHING EVER ASKED.**
 *  `edits.do_dimchain` and `edits.do_qdim` are full implementations with their own docstrings,
 *  `api.ts` types `"dimcont" | "dimbase" | "qdim"` in the op union, and `handlers.ts` maps the
 *  ribbon buttons to modes of those names. The canvas had no branch for any of the three, so
 *  the mode armed, `defOf` found nothing, and the studio reported a command that WAS built as
 *  "not built yet". Same shape as STRETCH, and as `extra` being dropped from `runCmd`.
 *
 *  ⚠⚠ **THEY ARE NOT `dimKind`s.** The dimension flyout's eight kinds all draw from scratch and
 *  go through `do_draw`; these three take a dimension (or a selection) that is ALREADY THERE and
 *  go through an edit op. Putting them in `DIM_KIND` would drag them through the two-then-pull
 *  gesture whose whole purpose they exist to skip — a drafter dimensioning a run of eight
 *  openings picks two edges once and then clicks six times.
 *
 *  ⚠ `ref` is what keeps the selection alive: the draw loop clears it for every tool WITHOUT a
 *  ref, and all three of these measure something already picked. QDIM lists the kinds it can
 *  dimension so that a stray dimension in the selection does not arm it.
 */
export const CHAIN_KIND: Record<string, DrawDef> = {
  // ⚠⚠ **`[Select]` AND `<Select>` WERE TWO MORE PROMPTS THAT LIED, AND A TEST FOUND THEM.**
  // `test/keywords.test.ts` walks every prompt in `DRAWS`, `DIM_KIND`, `CHAIN_KIND` and both
  // given tables and fails on any keyword nothing answers — these two turned up the moment it
  // was written, which is exactly what it is for. In AutoCAD `Select` picks a DIFFERENT base
  // dimension to continue from, and it is the DEFAULT; here the base dimension comes from the
  // selection, because pdcad is noun-verb everywhere else. Nothing answers `S`, so it is not
  // offered — and `<Select>` goes with it, because an angle-bracket default promises that
  // pressing Enter does that thing.
  // ⚠ `Undo` stays: it is answered, generically, by the keyword branch in `Studio.finishDraw`.
  // ⚠ `Select` is ANSWERED now (2026-09-09) — it picks a different dimension to continue from,
  // which is what AutoCAD's own prompt offers and what a drafter needs when a run restarts on
  // the far side of an opening. It is not printed as `<Select>`: an angle-bracket default
  // promises that ENTER does that thing, and here Enter ends the command.
  dimcont: {
    kind: "DIMCONTINUE", points: 1, ref: ["DIMENSION"],
    prompts: ["Specify second extension line origin or [Undo/Select]:"],
  },
  dimbase: {
    kind: "DIMBASELINE", points: 1, ref: ["DIMENSION"],
    prompts: ["Specify second extension line origin or [Undo/Select]:"],
  },
  qdim: {
    kind: "QDIM", points: 1,
    ref: ["LINE", "LWPOLYLINE", "POLYLINE", "CIRCLE", "ARC"],
    prompts: ["Specify dimension line position, or "
              + ":"],
  },
};

/** ⚠ Re-exported from `givens.ts`, which is where every other piece of arc-and-circle
 *  arithmetic now lives. The canvas preview imports it from here; moving the definition rather
 *  than copying it is what stops the two drifting. */
export { circleThrough } from "./givens";

// ── commands that pick OBJECTS rather than points ────────────────────────────────────────────

export interface PickDef {
  /** What `/edit/` calls it. */
  op: string;
  /** How many objects it needs. ⚠ -1 means "until Escape" — AutoCAD's TRIM runs on. */
  objects: number;
  prompts: string[];
  /** A number asked for BEFORE the objects, as FILLET asks for its radius. */
  scalar?: { label: string; suffix: string; def: number; prompt: string };
  /** ⚠ Whether the click POINT matters as well as which object was hit. TRIM needs it — it says
   *  which piece to remove — and FILLET does not. */
  wantsPoint?: boolean;
  /** ⚠⚠ A SECOND click, after the object, that says which SIDE. Only OFFSET has one: TRIM's
   *  single click is both the object and the piece, while OFFSET's object and side are two
   *  different questions and AutoCAD asks them one after the other. */
  sidePoint?: boolean;
}

/** ⚠⚠ **THESE ARE A THIRD KIND OF COMMAND AND THE TABLE HAD NO ROOM FOR THEM.** `COMMANDS` acts
 *  on a selection that already exists and asks for POINTS; `DRAWS` creates from points. TRIM,
 *  EXTEND, FILLET and CHAMFER ask for OBJECTS, one at a time, after the command has started —
 *  which is AutoCAD's own grammar for all four and is why they sit in `PICK_OF` rather than
 *  `OP_OF` in the lifted chrome. */
export const PICKS: Record<string, PickDef> = {
  trim: {
    op: "trim", objects: -1, wantsPoint: true,
    prompts: ["Select object to trim or shift-select to extend or [cuTting edges/Crossing]:"],
  },
  extend: {
    op: "extend", objects: -1, wantsPoint: true,
    prompts: ["Select object to extend or shift-select to trim or [Boundary edges/Crossing]:"],
  },
  fillet: {
    op: "fillet", objects: 2,
    scalar: { label: "Radius", suffix: "", def: 0,
              prompt: "Specify fillet radius <{d}>:" },
    prompts: ["Select first object or [Undo/Polyline/Radius/Trim/Multiple]:",
              "Select second object or shift-select to apply corner:"],
  },
  // ⚠⚠ **OFFSET IS A LOOP OVER ONE OBJECT AT A TIME, AND IT USED NOT TO BE.** This app took a
  // whole selection and ONE side point for all of it — its own invention, and wrong on the most
  // ordinary use there is: offsetting a wall's two lines outwards means one click on each side,
  // not one click for both. AutoCAD asks *"Select object to offset"*, then *"Specify point on
  // side to offset"*, and repeats until Escape. ⚠ It ignores a prior selection for the same
  // reason AutoCAD does: the object it acts on is the one you just pointed at.
  offset: {
    op: "offset", objects: -1, wantsPoint: true, sidePoint: true,
    scalar: { label: "Distance", suffix: "", def: 0,
              prompt: "Specify offset distance or [Through/Erase/Layer] <{d}>:" },
    // ⚠⚠ AutoCAD prints `[Exit/Undo]` and `[Exit/Multiple/Undo]` here. NOTHING ANSWERS THEM in
    // this app — Exit is Enter or Escape and Undo is Ctrl+Z, both of which work — so they are
    // not printed. That is the rule the 31 removed keywords were removed for, and
    // `test/keywords.test.ts` enforces it on these prompts too.
    prompts: ["Select object to offset or <Exit>:",
              "Specify point on side to offset:"],
  },
  chamfer: {
    op: "chamfer", objects: 2,
    scalar: { label: "Distance", suffix: "", def: 0,
              prompt: "Specify chamfer distance <{d}>:" },
    prompts: ["Select first line or [Undo/Polyline/Distance/Angle/Trim/mEthod/Multiple]:",
              "Select second line or shift-select to apply corner:"],
  },
  // ⚠⚠ **BLEND IS A PICK COMMAND FOR THE SAME REASON FILLET IS: WHICH END.** Both curves have
  // two, and the pair a drafter meant is decided by where they clicked and by nothing else. It
  // has no scalar — AutoCAD asks for Tangent or Smooth, and only Tangent is built (see
  // `edits.do_blend`, which refuses Smooth by name rather than quietly giving the G1 answer).
  blend: {
    op: "blend", objects: 2,
    prompts: ["Select first object or [CONtinuity]:",
              "Select second object:"],
  },
};

/** The handles whose geometry could possibly cross `target`'s, capped.
 *
 * ⚠⚠ **AutoCAD'S MODERN TRIM USES EVERY VISIBLE OBJECT AS A CUTTING EDGE, AND SENDING 3,767
 * HANDLES WOULD MAKE THE SERVER LOAD ALL OF THEM.** The bounding boxes are already here in the
 * display list, and an object whose box does not touch the target's cannot cross it — so the
 * filter is exact, not a heuristic, and it is free.
 * ⚠ The cap is REPORTED when it bites, because a trim that silently ignored the cutting edge
 * you meant is the worst possible version of this.
 */
// ⚠ Only the first four columns are read here, so the type says so — a full `DisplayList`
// row grew a linetype index in FORMAT 16 and a five-element tuple stopped accepting it.
export function nearbyHandles(dl: { polys: readonly (readonly [number, number, number[],
                                                              string, ...unknown[]])[] },
                              target: string, cap = 400): { handles: string[]; capped: boolean } {
  const box: Record<string, [number, number, number, number]> = {};
  for (const [, , pts, owner] of dl.polys) {
    const b = box[owner] ?? (box[owner] = [Infinity, Infinity, -Infinity, -Infinity]);
    for (let i = 0; i + 1 < pts.length; i += 2) {
      if (pts[i] < b[0]) b[0] = pts[i];
      if (pts[i + 1] < b[1]) b[1] = pts[i + 1];
      if (pts[i] > b[2]) b[2] = pts[i];
      if (pts[i + 1] > b[3]) b[3] = pts[i + 1];
    }
  }
  const t = box[target];
  if (!t) return { handles: [], capped: false };
  // ⚠ A hair of slack, so an edge that touches the target exactly at its own endpoint — which
  // is most of a real drawing — is not excluded by a floating-point tie.
  const pad = Math.max(1e-6, (t[2] - t[0] + t[3] - t[1]) * 1e-6);
  const out: string[] = [];
  for (const [h, b] of Object.entries(box)) {
    if (h === target) continue;
    if (b[0] > t[2] + pad || b[2] < t[0] - pad || b[1] > t[3] + pad || b[3] < t[1] - pad) continue;
    out.push(h);
  }
  return { handles: out.slice(0, cap), capped: out.length > cap };
}

/** EXTEND's candidate boundaries — which are NOT TRIM's cutting edges, and using TRIM's was a
 *  bug that made the command almost unusable.
 *
 *  ⚠⚠⚠ **A BOUNDARY YOU HAVE TO REACH IS, BY DEFINITION, ONE YOU DO NOT TOUCH YET.**
 *  `nearbyHandles` keeps objects whose bounding box overlaps the target's — exact for TRIM,
 *  where a cutter must cross the object — and it therefore throws away exactly the objects
 *  EXTEND exists to find. Measured on drawing 10951: a 200-unit line at y 9000200 spanning
 *  x 9000200-9000400, with a vertical line 100 units beyond its right end, got `edges: []` and
 *  the server answered *"no boundary beyond that end"*. The only boundaries that ever survived
 *  were ones already touching the object's box — which mostly do not need extending to.
 *  ⚠ `qa/bench_actions.py` reported this as a 24.7 ms EXTEND with the note `1 found`. It was
 *  the scratch geometry's own time under EXTEND's name; the command never did anything.
 *
 *  ⇒ The extension continues the END SEGMENT the drafter clicked, so the exact candidate set is
 *  the objects whose box straddles that infinite line and lies beyond that end. For a CURVED
 *  target the extension stays on its own circle instead, so the test is against the circle
 *  through its first, middle and last point. Both are exact filters, like TRIM's; neither is a
 *  margin anyone has to tune.
 *  ⚠ Same cap as TRIM, and reported for the same reason.
 */
export function reachHandles(dl: { polys: readonly (readonly [number, number, number[],
                                                              string, ...unknown[]])[] },
                             target: string, at: { x: number; y: number },
                             cap = 400): { handles: string[]; capped: boolean } {
  const box: Record<string, [number, number, number, number]> = {};
  const tp: [number, number][] = [];
  for (const [, , pts, owner] of dl.polys) {
    const b = box[owner] ?? (box[owner] = [Infinity, Infinity, -Infinity, -Infinity]);
    for (let i = 0; i + 1 < pts.length; i += 2) {
      if (pts[i] < b[0]) b[0] = pts[i];
      if (pts[i + 1] < b[1]) b[1] = pts[i + 1];
      if (pts[i] > b[2]) b[2] = pts[i];
      if (pts[i + 1] > b[3]) b[3] = pts[i + 1];
      if (owner === target) tp.push([pts[i], pts[i + 1]]);
    }
  }
  const t = box[target];
  if (!t) return { handles: [], capped: false };
  if (tp.length < 2) return nearbyHandles(dl, target, cap);
  const tol = Math.max(1e-6, (t[2] - t[0] + t[3] - t[1]) * 1e-6);
  const a = tp[0], z = tp[tp.length - 1];
  // ⚠ Is it straight? A LINE and a tessellated ARC are both just points here, and the two need
  // different tests — so the geometry is asked rather than the entity kind, which this list
  // does not carry.
  let bend = 0;
  {
    const vx = z[0] - a[0], vy = z[1] - a[1];
    const vl = Math.hypot(vx, vy);
    if (vl > 0) {
      for (const [x, y] of tp) {
        const d = Math.abs((x - a[0]) * (-vy / vl) + (y - a[1]) * (vx / vl));
        if (d > bend) bend = d;
      }
    } else { bend = Infinity; }
  }
  const out: string[] = [];
  const corners = (b: [number, number, number, number]) =>
    [[b[0], b[1]], [b[2], b[1]], [b[0], b[3]], [b[2], b[3]]] as [number, number][];
  if (bend <= tol) {
    const dz = (at.x - z[0]) ** 2 + (at.y - z[1]) ** 2;
    const da = (at.x - a[0]) ** 2 + (at.y - a[1]) ** 2;
    const atZ = dz <= da;
    const end = atZ ? z : a;
    const prev = atZ ? tp[tp.length - 2] : tp[1];
    let ux = end[0] - prev[0], uy = end[1] - prev[1];
    const ul = Math.hypot(ux, uy);
    if (!(ul > 0)) return nearbyHandles(dl, target, cap);
    ux /= ul; uy /= ul;
    const nx = -uy, ny = ux;
    for (const [h, b] of Object.entries(box)) {
      if (h === target) continue;
      let lo = Infinity, hi = -Infinity, far = -Infinity;
      for (const [cx, cy] of corners(b)) {
        const side = (cx - end[0]) * nx + (cy - end[1]) * ny;
        if (side < lo) lo = side;
        if (side > hi) hi = side;
        const along = (cx - end[0]) * ux + (cy - end[1]) * uy;
        if (along > far) far = along;
      }
      if (lo > tol || hi < -tol) continue;     // never crosses the line the extension runs on
      if (far <= tol) continue;                // entirely behind the end that was clicked
      out.push(h);
    }
  } else {
    // ⚠ The circle through first, middle and last. A boundary can only stop a curved extension
    // by meeting that circle, so a box whose nearest corner is outside it or whose farthest is
    // inside it cannot.
    const m = tp[Math.floor(tp.length / 2)];
    const d = 2 * (a[0] * (m[1] - z[1]) + m[0] * (z[1] - a[1]) + z[0] * (a[1] - m[1]));
    if (Math.abs(d) < 1e-12) return nearbyHandles(dl, target, cap);
    const sa = a[0] ** 2 + a[1] ** 2, sm = m[0] ** 2 + m[1] ** 2, sz = z[0] ** 2 + z[1] ** 2;
    const cx0 = (sa * (m[1] - z[1]) + sm * (z[1] - a[1]) + sz * (a[1] - m[1])) / d;
    const cy0 = (sa * (z[0] - m[0]) + sm * (a[0] - z[0]) + sz * (m[0] - a[0])) / d;
    const R = Math.hypot(a[0] - cx0, a[1] - cy0);
    for (const [h, b] of Object.entries(box)) {
      if (h === target) continue;
      const gx = Math.max(b[0] - cx0, 0, cx0 - b[2]);
      const gy = Math.max(b[1] - cy0, 0, cy0 - b[3]);
      const near = Math.hypot(gx, gy);
      let farC = 0;
      for (const [px, py] of corners(b)) farC = Math.max(farC, Math.hypot(px - cx0, py - cy0));
      if (near > R + tol || farC < R - tol) continue;
      out.push(h);
    }
  }
  return { handles: out.slice(0, cap), capped: out.length > cap };
}

/** PEDIT options that need a POINT on the drawing rather than a number.
 *
 * ⚠ "Which segment" is a PLACE, not an index. AutoCAD's PEDIT walks a marker along the vertices
 * with Next/Previous because its command line has no way to point at one; a canvas does, so the
 * drafter clicks the segment they mean and the server resolves it to an index. Asking for "2"
 * would be reproducing a 1982 workaround on hardware that does not need it. */
export const PEDIT_POINT = new Set(["addvertex", "delvertex", "arc", "straight"]);

/** AutoCAD's own wording for each. */
export const PEDIT_PROMPT: Record<string, string> = {
  addvertex: "Specify the point on the polyline to add a vertex at:",
  delvertex: "Specify the vertex to remove:",
  arc: "Specify a point for the arc to pass through:",
  straight: "Specify a point on the segment to straighten:",
};

/** PEDIT's option letters, AutoCAD's where they exist and unambiguous where they do not.
 *
 * ⚠ AutoCAD nests Insert/Delete/Straighten under `E` (Edit vertex), which walks a marker along
 * the polyline with Next/Previous because a 1982 command line cannot point at a segment. A
 * canvas can, so those three are promoted to top-level letters and the drafter clicks the place
 * instead of stepping to it. The letters AutoCAD DOES define — C, O, W, J, R — keep their
 * meanings exactly, because those are in a drafter's fingers.
 */
export const PEDIT_OPTION: Record<string, string> = {
  c: "close", close: "close",
  o: "open", open: "open",
  w: "width", width: "width",
  j: "join", join: "join",
  r: "reverse", reverse: "reverse",
  a: "arc", arc: "arc",
  s: "straight", straight: "straight", straighten: "straight",
  i: "addvertex", add: "addvertex", addvertex: "addvertex",
  d: "delvertex", del: "delvertex", delete: "delvertex", delvertex: "delvertex",
  // ⚠⚠ **`d` IS ALREADY DELETE VERTEX, SO DECURVE CANNOT HAVE IT.** AutoCAD's own PEDIT prompt
  // spells it `Decurve` and takes `D` — but AutoCAD puts Add/Delete vertex behind an `Edit
  // vertex` sub-mode, so the letter is free there and is not free here. `de` is the shortest
  // unambiguous prefix; the full words work for all of them, which is what a drafter who is
  // unsure will type.
  f: "fit", fit: "fit",
  de: "decurve", decurve: "decurve",
  l: "ltype", lt: "ltype", ltype: "ltype", ltypegen: "ltype",
};

/** The SPLINEDIT options that need a point on the canvas before they can run. */
export const SPLINEDIT_POINT = new Set(["addcv", "delcv"]);

export const SPLINEDIT_PROMPT: Record<string, string> = {
  addcv: "Specify the point to add a control vertex at:",
  delcv: "Specify the control vertex to remove:",
};

/** SPLINEDIT's option letters. ⚠ AutoCAD nests these under `Fit data` and `Edit vertex`
 *  sub-menus for the same 1982 reason PEDIT does — a command line cannot point at a control
 *  vertex, so it walks a marker to it. A canvas can, so `A` and `D` are promoted and the drafter
 *  clicks. `C`, `O`, `P` and `E`(degree) keep AutoCAD's meanings. */
export const SPLINEDIT_OPTION: Record<string, string> = {
  c: "close", close: "close",
  o: "open", open: "open",
  p: "purge", purge: "purge",
  f: "refit", fit: "refit", refit: "refit",
  e: "degree", d: "degree", degree: "degree",
  a: "addcv", add: "addcv", addcv: "addcv",
  x: "delcv", del: "delcv", delete: "delcv", delcv: "delcv",
};

/** How each op reads in the past tense. ⚠⚠ **THE COMMAND'S OWN VERB, NOT `${op}d`.** "8 could
 *  not be offsetd" is a typo the drafter sees; worse, "8 could not be moved" after an OFFSET
 *  names an operation they did not run and sends them looking for a MOVE that never happened.
 *  The fallback is still `${op}d`, which is right for `trim`/`extend`/`scale` and wrong-looking
 *  for exactly the ones listed here. */
export const VERB: Record<string, string> = {
  erase: "erased", copy: "copied", mirror: "mirrored", offset: "offset",
  pedit: "edited", pjoin: "joined", setprop: "changed", mview: "cut",
  //: ⚠ "spaced", not "moved" — DIMSPACE evens the gaps, and a drafter who reads "moved" looks
  //: for objects that went somewhere they did not ask for.
  dimspace: "spaced", leaderalign: "aligned", leadercollect: "collected",
  clipcopy: "copied", clippaste: "pasted",
  vpscale: "rescaled", hatchedit: "restyled", block: "made into a block",
  splinedit: "edited",
  // ⚠ "6 objects arrayd" — seen in a real transcript. This table exists for exactly that, and
  // two new ops were added to `OPS` without a line here.
  array: "arrayed", stretch: "stretched",
  // ⚠ "deleted", not "overkilld". OVERKILL's own sentence in `commit` adds the merge count,
  // because "5 objects deleted" hides the half of the command a drafter most wants to see.
  overkill: "deleted",
};


/** The drawing's bigger side, BOUNDED — what a sensible text height, offset or radius default is
 *  derived from.
 *
 *  ⚠⚠⚠ **ONE BAD COORDINATE POISONS EVERY DEFAULT AFTER IT, AND THE DAMAGE FEEDS ITSELF.** Found
 *  on drawing 10448: `extents[2]` read **2e+210**, so the height ask offered
 *  `Specify height <1e+208>:`; a drafter pressing Enter gets a TEXT stored with that height, and
 *  THAT entity pushes the extents further out for the next command. Seven entities at 2e+210 came
 *  out of that loop before anything noticed. It also blanks ZOOM Extents, which dutifully fits the
 *  whole 2e+210 span and leaves the real drawing a sub-pixel dot.
 *
 *  ⚠ 1e9 drawing units is a thousand kilometres in millimetres — larger than any drawing this
 *  office has, and small enough that a runaway cannot survive it. NaN and Infinity fall to 0,
 *  which every `defaultOf` already handles with `Math.max(1, extent)`.
 */
export function sanityExtent(extents: readonly number[] | null | undefined): number {
  if (!extents || extents.length < 4) return 0;
  const w = Number.isFinite(extents[2] - extents[0]) ? Math.abs(extents[2] - extents[0]) : 0;
  const h = Number.isFinite(extents[3] - extents[1]) ? Math.abs(extents[3] - extents[1]) : 0;
  const big = Math.max(w, h);
  const small = Math.min(w, h);
  // ⚠⚠ **THE FAILURE MODE IS ONE AXIS, NOT BOTH**, and a flat cap is not enough for it. Drawing
  // 10919 measured 2.5e+11 across and 9.2e+06 up — a plan 377 m wide with one runaway X. Capped
  // at 1e9 the default was still 5,000,000 units, so MTEXT went on refusing every box as "too
  // narrow for text of this height". A drawing 100x longer than it is tall is a corrupt axis,
  // not a drawing: no plan, section or site layout this office has comes near that, so the
  // SANE axis is the answer.
  const use = small > 0 && big > 100 * small ? small : big;
  return use > 0 ? Math.min(1e9, use) : 0;
}

/** The kinds `Canvas2D` can draw as a SETTLED band — the shapes whose renderer needs nothing
 *  but the points that were posted.
 *
 * ⚠⚠ **DELIBERATELY THE FOUR THE BAND ALREADY DRAWS, AND NOT ONE MORE.** `Canvas2D`'s band has
 * an explicit branch for CIRCLE, RECT and ARC and falls through to a polyline for everything
 * else — which is right for LINE and LWPOLYLINE and WRONG for anything whose picture is not its
 * click points: an ELLIPSE drawn as a two-point line, a TEXT drawn as nothing, a DIMENSION drawn
 * as the segment being measured rather than as the dimension. A ghost that shows the wrong shape
 * is worse than the blink it replaces, because the blink is honest.
 * ⚠ RECT covers RECTANG and MVIEW; both post two corners and `rectCorners` turns them into the
 * same four points the commit uses.
 */
// ⚠ POLYLINE was in this list and is not a `DrawDef` kind — no draw tool makes one, so its
// ghost could never have fired. `test/ghost.test.ts` caught it before it shipped as a line
// nobody could explain and nobody could reproduce.
//: ⚠ ELLIPSE and POINT joined once `Canvas2D.drawBand` could draw them SETTLED — before that
//: an ellipse fell through to the polyline fallback and was a bent line, which is a fine thing
//: to drag and a lie to hold on screen afterwards. Both are held to `qa/ghost_shot.py`, which
//: photographs the held shape against the one the server sends.
export const GHOST_KINDS: ReadonlySet<string> = new Set(
  ["LINE", "LWPOLYLINE", "CIRCLE", "RECT", "ARC", "ELLIPSE", "POINT"]);
